import { useState, useEffect } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { useForm } from "react-hook-form"
import { useAuth } from "@/contexts/AuthContext"
import { useTheme } from "@/components/theme-provider"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardFooter } from "@/components/ui/card"
import { Loader2, Sparkles, Shield, AlertTriangle, Users, ShieldCheck, Scale, FileSearch, BookOpen, Eye, ChevronDown, CheckCircle2 } from "lucide-react"
import { usePublicTenantBootstrap } from "@/hooks/use-tenant-settings"
import { defaultProductProfile, liteProductProfile } from "@/lib/product-profile"
import { api } from "@/lib/api"
import { isApiError } from "@/lib/api/types"
import {
  clearSessionProductProfileOverride,
  resolveProductProfileOverrideQuery,
  setSessionProductProfileOverride,
} from "@/lib/product-profile-session"
import { motion, AnimatePresence } from "framer-motion"

interface LoginFormValues {
  email: string
  password: string
}

interface LoginPageProps {
  entryMode?: "standard" | "lite"
}

// Cold-start detection: timeout (408) or gateway errors (502/503/504) suggest
// the Cloud Run container is booting. We auto-retry once with a longer timeout
// and show a "waking up" message instead of an error.
function isColdStartError(err: unknown): boolean {
  if (isApiError(err)) {
    return err.status === 408 || err.status === 502 || err.status === 503 || err.status === 504
  }
  return false
}

const COLD_START_TIMEOUT = 90_000 // 90s for retry during cold boot

const SHOW_DEMO_ROLE_CHOOSER =
  import.meta.env.VITE_SHOW_DEMO_ROLE_CHOOSER === "true" ||
  import.meta.env.VITE_SHOW_DEMO_LOGIN === "true"
const ENABLE_DEMO_ROLE_AUTOFILL = import.meta.env.VITE_ENABLE_DEMO_ROLE_AUTOFILL === "true"

const DEMO_PROFILES = [
  {
    id: "admin",
    role: "Admin",
    name: "Sarah Chen",
    description: "Full platform access, user management, queue admin",
    icon: ShieldCheck,
    color: "text-red-400",
    bgColor: "bg-red-500/10 hover:bg-red-500/15 border-red-500/20",
    badgeColor: "bg-red-500/20 text-red-400",
  },
  {
    id: "attorney",
    role: "Attorney",
    name: "Michael Torres",
    description: "Matter strategy, review oversight, timeline, and production handoff",
    icon: Scale,
    color: "text-blue-400",
    bgColor: "bg-blue-500/10 hover:bg-blue-500/15 border-blue-500/20",
    badgeColor: "bg-blue-500/20 text-blue-400",
  },
  {
    id: "paralegal",
    role: "Paralegal",
    name: "Jessica Park",
    description: "Document intake, processing, review assignments",
    icon: FileSearch,
    color: "text-emerald-400",
    bgColor: "bg-emerald-500/10 hover:bg-emerald-500/15 border-emerald-500/20",
    badgeColor: "bg-emerald-500/20 text-emerald-400",
  },
  {
    id: "reviewer",
    role: "Reviewer",
    name: "David Kim",
    description: "Matter review queues, document decisions, and QC follow-up",
    icon: BookOpen,
    color: "text-amber-400",
    bgColor: "bg-amber-500/10 hover:bg-amber-500/15 border-amber-500/20",
    badgeColor: "bg-amber-500/20 text-amber-400",
  },
  {
    id: "readonly",
    role: "Read Only",
    name: "Emily Watson",
    description: "View documents and search — no edit access",
    icon: Eye,
    color: "text-slate-400",
    bgColor: "bg-slate-500/10 hover:bg-slate-500/15 border-slate-500/20",
    badgeColor: "bg-slate-500/20 text-slate-400",
  },
] as const

function resolvePostLoginDestination(locationState: unknown): string {
  const from = (locationState as {
    from?: {
      pathname?: string
      search?: string
      hash?: string
    }
  })?.from

  if (!from?.pathname) return "/"

  const destination = `${from.pathname}${from.search || ""}${from.hash || ""}`
  if (
    destination.startsWith("/login") ||
    destination.startsWith("/signup") ||
    destination.startsWith("/lite")
  ) {
    return "/"
  }

  return destination
}

export default function LoginPage({ entryMode = "standard" }: LoginPageProps) {
  const { register, handleSubmit: rhfSubmit, formState: { errors: fieldErrors, isSubmitting } } = useForm<LoginFormValues>({ mode: "onBlur" })
  const [error, setError] = useState("")
  const [coldStartMessage, setColdStartMessage] = useState("")
  const [showDemoPanel, setShowDemoPanel] = useState(true)
  const [selectedDemoRole, setSelectedDemoRole] = useState<string | null>(null)
  const { login, isAuthenticated, isLoading } = useAuth()
  const { activeTheme } = useTheme()
  const location = useLocation()
  const navigate = useNavigate()
  const profileOverride = resolveProductProfileOverrideQuery(
    new URLSearchParams(location.search).get("profile")
  )
  const isLiteEntry = entryMode === "lite" || profileOverride === "eca_lite"
  const { data: publicTenantBootstrap } = usePublicTenantBootstrap({
    enabled: !isLiteEntry,
  })
  const shellProfile = isLiteEntry ? liteProductProfile : publicTenantBootstrap?.product_profile
  const shellLabel = shellProfile?.label || defaultProductProfile.label
  const shellDescription = shellProfile?.description || defaultProductProfile.description
  const brand = activeTheme.brand
  const appVersion = import.meta.env.VITE_APP_VERSION || "1.0.0"
  const signupHref = isLiteEntry ? "/lite/signup" : "/signup"
  const alternateEntryHref = isLiteEntry ? "/login" : "/lite/login"
  const alternateEntryLabel = isLiteEntry ? "Use standard sign in" : "Open Lite demo"

  // Get the page they were trying to visit (or default to dashboard)
  const from = resolvePostLoginDestination(location.state)

  useEffect(() => {
    if (isLiteEntry) {
      setSessionProductProfileOverride("eca_lite")
      return
    }

    clearSessionProductProfileOverride()
  }, [isLiteEntry])

  // If already logged in, redirect to intended page
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate(from, { replace: true })
    }
  }, [isAuthenticated, isLoading, from, navigate])

  const doLogin = async (credentials: { email: string; password: string; demo_role?: string }, isRetry: boolean) => {
    const data = isRetry
      ? await api.auth.login(credentials, { timeout: COLD_START_TIMEOUT })
      : await api.auth.login(credentials)
    const user = {
      id: data.user.id,
      email: data.user.email,
      full_name: data.user.full_name,
      role: data.user.role || 'user',
      permissions: data.user.permissions || [],
      capabilities: data.user.capabilities || [],
      ai_guidance_enabled: data.user.ai_guidance_enabled,
    }
    login(user)
  }

  const onSubmit = async (data: LoginFormValues) => {
    setError("")
    setColdStartMessage("")
    const loginRequest = selectedDemoRole
      ? { ...data, demo_role: selectedDemoRole }
      : data

    try {
      await doLogin(loginRequest, false)
    } catch (err: unknown) {
      // Cold-start: auto-retry once with extended timeout
      if (isColdStartError(err)) {
        setColdStartMessage("Establishing secure connection - server is waking up...")
        try {
          await doLogin(loginRequest, true)
          return // success on retry
        } catch (retryErr: unknown) {
          setColdStartMessage("")
          if (isApiError(retryErr) && retryErr.status === 400) {
            setError(retryErr.message)
          } else
          if (isApiError(retryErr) && retryErr.status === 401) {
            setError("Your email or password didn’t match. Please try again.")
          } else {
            setError("The server is still waking up. Please try again in about 30 seconds.")
          }
          return
        }
      }

      if (isApiError(err) && err.status === 400) {
        setError(err.message)
      } else
      if (isApiError(err) && err.status === 401) {
        setError("Your email or password didn’t match. Please try again.")
      } else {
        setError(err instanceof Error ? err.message : "We couldn’t sign you in. Please try again.")
      }
    } finally {
      setColdStartMessage("")
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background relative overflow-hidden py-8">
      <a href="#main" className="pointer-events-none fixed left-4 top-4 z-50 rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground opacity-0 shadow-lg transition focus:pointer-events-auto focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">Skip to content</a>
      {/* Forensic grid background */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" 
           style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, hsl(var(--foreground)) 1px, transparent 0)', backgroundSize: '40px 40px' }} />
      
      {/* Scanning line animation */}
      <div className="pointer-events-none fixed inset-0 z-10 overflow-hidden opacity-[0.05]">
        <div className="absolute top-0 left-0 w-full h-[2px] bg-primary animate-scan" />
      </div>

      <motion.main
        id="main"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md z-20"
      >
        <Card className="bg-card/80 dark:bg-black/40 backdrop-blur-sm border-border/40 shadow-2xl relative overflow-hidden group">
          {/* Top accent tape */}
          <div className="absolute top-0 left-0 w-full h-1 bg-primary shadow-[0_0_15px_rgba(var(--primary),0.5)]" />
          
          <CardHeader className="pt-10 pb-8 text-center">
            <p className="text-[11px] font-mono font-bold uppercase tracking-[0.35em] text-primary/70">
              Secure Access
            </p>
            <div className="flex justify-center mb-6">
              {brand.wordmarkUrl ? (
                <div className="relative rounded-2xl border border-border/40 bg-muted/15 px-6 py-5 shadow-[0_0_30px_rgba(var(--primary),0.08)] backdrop-blur-sm">
                  <img
                    src={brand.wordmarkUrl}
                    alt={`${brand.appName} wordmark`}
                    className="mx-auto max-h-10 w-auto object-contain"
                  />
                  <div className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full bg-success border-2 border-background" />
                </div>
              ) : (
                <div className="relative">
                  <div className="h-16 w-16 rounded-lg bg-primary flex items-center justify-center shadow-[0_0_30px_rgba(var(--primary),0.2)] border border-border group-hover:scale-105 transition-transform duration-500 overflow-hidden">
                    {brand.logoUrl ? (
                      <img
                        src={brand.logoUrl}
                        alt={`${brand.appName} logo`}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <Sparkles className="h-10 w-10 text-primary-foreground" />
                    )}
                  </div>
                  <div className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full bg-success border-2 border-background" />
                </div>
              )}
            </div>
            <h1 className="text-xl font-bold tracking-tight text-foreground">
              {brand.appName}
            </h1>
            <CardDescription className="text-xs font-mono font-bold uppercase tracking-[0.25em] text-muted-foreground/60 mt-3">
              {shellLabel} &bull; v{appVersion}
            </CardDescription>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground max-w-sm mx-auto">
              {shellDescription}
            </p>
          </CardHeader>

          <form onSubmit={rhfSubmit(onSubmit)} aria-label="Login form">
            <CardContent className="space-y-6 px-8">
              {error && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  role="alert"
                  className="bg-destructive/10 border border-destructive/30 text-destructive text-xs font-medium p-3 rounded flex items-center gap-3"
                >
                  <AlertTriangle className="h-4 w-4" />
                  {error}
                </motion.div>
              )}

              {coldStartMessage && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  role="status"
                  aria-live="polite"
                  className="bg-amber-500/10 border border-amber-500/30 text-amber-600 dark:text-amber-400 text-xs font-mono font-bold uppercase tracking-wide p-3 rounded flex items-center gap-3"
                >
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {coldStartMessage}
                </motion.div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wide text-muted-foreground/70">
                  Email
                </Label>
                <div className="relative">
                  <Input
                    id="email"
                    type="email"
                    placeholder="user@organization.com"
                    className="bg-muted/20 border-border/40 h-11 text-sm font-medium tracking-tight focus:bg-muted/30 focus:border-primary/40 transition-all pl-10"
                    {...register("email", { required: "Email is required" })}
                    aria-required="true"
                    aria-invalid={!!fieldErrors.email}
                    autoComplete="email"
                  />
                  <Shield className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60" />
                </div>
                {fieldErrors.email && (
                  <p className="text-destructive text-xs">{fieldErrors.email.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wide text-muted-foreground/70">
                    Password
                  </Label>
                </div>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  className="bg-muted/20 border-border/40 h-11 text-sm font-medium tracking-tight focus:bg-muted/30 focus:border-primary/40 transition-all px-4"
                  {...register("password", { required: "Password is required" })}
                  aria-required="true"
                  aria-invalid={!!fieldErrors.password}
                  autoComplete="current-password"
                />
                {fieldErrors.password && (
                  <p className="text-destructive text-xs">{fieldErrors.password.message}</p>
                )}
              </div>
            </CardContent>

            <CardFooter className="px-8 pb-6 pt-4 flex flex-col gap-6">
              <Button
                className="w-full h-12 font-bold uppercase tracking-[0.25em] text-xs shadow-[0_0_20px_rgba(var(--primary),0.1)] hover:shadow-[0_0_25px_rgba(var(--primary),0.2)] transition-all gap-2"
                type="submit"
                loading={isSubmitting}
              >
                {!isSubmitting && <Shield className="h-4 w-4" aria-hidden="true" />}
                {coldStartMessage ? "Connecting..." : isSubmitting ? "Authenticating..." : "Sign In"}
              </Button>

              <div className="w-full space-y-3 pt-4 border-t border-border/20">
                <p className="text-sm text-center text-muted-foreground">
                  New to ECA?{" "}
                  <Link
                    to={signupHref}
                    className="text-primary font-medium hover:underline transition-colors"
                  >
                    {isLiteEntry ? "Create your Lite workspace" : "Create your workspace"}
                  </Link>
                </p>
                <p className="text-xs text-center text-muted-foreground/70">
                  <Link
                    to={alternateEntryHref}
                    className="font-medium text-muted-foreground hover:text-foreground hover:underline transition-colors"
                  >
                    {alternateEntryLabel}
                  </Link>
                </p>
              </div>
            </CardFooter>
          </form>
        </Card>

        {SHOW_DEMO_ROLE_CHOOSER && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="mt-4"
        >
          <Card className="bg-card/60 dark:bg-black/30 backdrop-blur-sm border-border/30 shadow-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setShowDemoPanel(!showDemoPanel)}
              className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-muted/20 transition-colors"
              aria-expanded={showDemoPanel}
              aria-controls="demo-login-panel"
            >
              <div className="flex items-center gap-2.5">
                <Users className="h-4 w-4 text-primary/70" />
                <span className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-muted-foreground/80">
                  Demo Role Chooser
                </span>
              </div>
              <motion.div
                animate={{ rotate: showDemoPanel ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <ChevronDown className="h-4 w-4 text-muted-foreground/70" />
              </motion.div>
            </button>

            <AnimatePresence initial={false}>
              {showDemoPanel && (
                <motion.div
                  id="demo-login-panel"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                  className="overflow-hidden"
                >
                  <div className="px-4 pb-4 space-y-2">
                    <div className="px-1 pb-2">
                      <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-muted-foreground/65">
                        Select a demo role, then enter the shared demo email and password manually in the sign-in form.
                      </p>
                      <p className="mt-2 text-[10px] font-mono uppercase tracking-[0.14em] text-muted-foreground/45">
                        {ENABLE_DEMO_ROLE_AUTOFILL
                          ? "This build allows demo-role assist, but credentials still require form submit."
                          : "This build requires manual credential entry. No autofill. No auto-submit."}
                      </p>
                    </div>
                    {DEMO_PROFILES.map((profile, index) => {
                      const Icon = profile.icon
                      const isSelected = selectedDemoRole === profile.id

                      return (
                        <motion.button
                          key={profile.id}
                          type="button"
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05, duration: 0.2 }}
                          onClick={() => setSelectedDemoRole((current) => current === profile.id ? null : profile.id)}
                          className={`
                            w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg border transition-all duration-200
                            ${profile.bgColor}
                            ${isSelected ? "ring-1 ring-primary/50 scale-[0.98] border-primary/40" : "cursor-pointer"}
                          `}
                          aria-pressed={isSelected}
                          aria-label={`Select demo role ${profile.name} (${profile.role})`}
                        >
                          <div className={`shrink-0 h-8 w-8 rounded-md flex items-center justify-center ${profile.badgeColor}`}>
                            {isSelected ? (
                              <CheckCircle2 className="h-4 w-4" />
                            ) : (
                              <Icon className="h-4 w-4" />
                            )}
                          </div>
                          <div className="flex-1 text-left min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-bold ${profile.color}`}>
                                {profile.role}
                              </span>
                              <span className="text-[10px] text-muted-foreground/70 font-medium truncate">
                                {profile.name}
                              </span>
                            </div>
                            <p className="text-[10px] text-muted-foreground/60 leading-tight truncate">
                              {profile.description}
                            </p>
                          </div>
                          {isSelected && (
                            <div className="shrink-0">
                              <span className="rounded-full border border-primary/40 bg-primary/10 px-2 py-1 text-[10px] font-mono uppercase tracking-[0.18em] text-primary">
                                Selected
                              </span>
                            </div>
                          )}
                        </motion.button>
                      )
                    })}
                    {selectedDemoRole && (
                      <div className="mx-1 mt-3 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-[11px] font-mono uppercase tracking-[0.14em] text-primary/80">
                        Selected role: {DEMO_PROFILES.find((profile) => profile.id === selectedDemoRole)?.role}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>
        </motion.div>
        )}

        {/* Footer legal text */}
        <div className="mt-6 text-center space-y-1">
          <p className="text-xs font-mono uppercase tracking-wide text-muted-foreground/60">
            {brand.shortName} &bull; {shellLabel}
          </p>
          <p className="text-xs font-mono text-muted-foreground/20 uppercase tracking-wide">
            Secure workspace access &bull; Activity is logged for audit history
          </p>
        </div>
      </motion.main>
    </div>
  )
}
